import type { RouteRecordRaw } from 'vue-router'

import { registerCommerceMessages } from './i18n'

import { registerReportMessages } from '@/features/reports'
import { DEFAULT_LOCALE, SUPPORTED_LOCALES, i18n, type SupportedLocale } from '@/shared/i18n'

// Module 27's routes (ORP-18949) — only screens that genuinely render report data ship a
// route (the honesty rule: a hollow shell is not a screen). Of the five module-27 routes v4
// still lacked (the other six resolve through 08's seeded report-page registrations):
//
// - `AppleSearchAds` SHIPS: its Performance Breakdown section renders real report data
//   through 08's `ReportPage`; the three ORP-18952 panels are clearly-marked pending extras.
//   DEEP-LINK ONLY — v3's whole App Analytics nav branch is commented out on live main
//   (`DefaultLayout.vue:481–501`) and product has not revived it, so no nav entry (nav.ts).
// - `Benchmark` SHIPS (ORP-18954): its panel owns the custom percentile sidecar without feature
//   global Axios hooks and composes the shared report/date/table surfaces.
// - `CreativeWatchlist` SHIPS (ORP-18955): the Meta report surface and abortable Google ATC
//   dataset share one state-preserving host. Definition creation remains owned by module 30.
// - `ProductInsights` SHIPS (ORP-18953): the KPI cards + Demand Trend landed as `component`
//   sections of the PRODUCT_INSIGHTS layout, so the page genuinely renders. Nav stays
//   unwired here — its leaf must compose into 08's `ecommerceGroup` (nav.ts), a shell edit.
// - `PerformanceOverview` SHIPS (ORP-18950): the pivot model, toolbar and saved-view flow run
//   end-to-end; the three ORP-18951 grids render as clearly-marked pending placeholders (the
//   scorecards "being rebuilt ≠ empty data" idiom) — the page genuinely renders report data.
//
// Layout registration rides `beforeEnter`, not a module side effect: the configs stay
// co-chunked with the page (out of the boot chunk), and the registry entry exists before the
// page renders. 08's `reports.*` bundle loads too — on a cold deep link 08's own routes never
// ran, and `ReportPage` renders `reports.page.*` copy.

function activeLocale(): SupportedLocale {
  const locale = i18n.global.locale.value
  return (SUPPORTED_LOCALES as readonly string[]).includes(locale)
    ? (locale as SupportedLocale)
    : DEFAULT_LOCALE
}

export const commerceRoutes: RouteRecordRaw[] = [
  {
    path: 'app-analytics/app-acquisition',
    name: 'MobileAppAcquisition',
    component: () => import('./components/pages/AppAcquisitionPage.vue'),
    meta: { navKey: 'appAcquisition', titleKey: 'commerceAnalytics.appAcquisition.title' },
    beforeEnter: async () => {
      const locale = activeLocale()
      const [{ registerCommerceLayouts }] = await Promise.all([
        import('./flows/registerCommerceLayouts'),
        registerReportMessages(locale),
        registerCommerceMessages(locale),
      ])
      registerCommerceLayouts()
    },
  },
  {
    path: 'benchmark',
    name: 'Benchmark',
    component: () => import('./components/pages/BenchmarkPage.vue'),
    meta: { navKey: 'benchmark', titleKey: 'commerceAnalytics.benchmark.title' },
    beforeEnter: async () => {
      const locale = activeLocale()
      const [{ registerCommerceLayouts }] = await Promise.all([
        import('./flows/registerCommerceLayouts'),
        registerReportMessages(locale),
        registerCommerceMessages(locale),
      ])
      registerCommerceLayouts()
    },
  },
  {
    // v3 path parity: `app-analytics/apple-search-ads` under the authenticated '/' parent.
    path: 'app-analytics/apple-search-ads',
    name: 'AppleSearchAds',
    component: () => import('./components/pages/AppleSearchAdsPage.vue'),
    meta: { navKey: 'appleSearchAds', titleKey: 'commerceAnalytics.appleSearchAds.title' },
    beforeEnter: async () => {
      const locale = activeLocale()
      const [{ registerCommerceLayouts }] = await Promise.all([
        import('./flows/registerCommerceLayouts'),
        registerReportMessages(locale),
        registerCommerceMessages(locale),
      ])
      registerCommerceLayouts()
    },
  },
  {
    // v3 path + name parity (`router/index.js` `ProductInsights`) — ORP-18953.
    path: 'ecommerce/product-insights',
    name: 'ProductInsights',
    component: () => import('./components/pages/ProductInsightsPage.vue'),
    meta: { navKey: 'productInsights', titleKey: 'commerceAnalytics.productInsights.title' },
    beforeEnter: async () => {
      const locale = activeLocale()
      const [{ registerCommerceLayouts }] = await Promise.all([
        import('./flows/registerCommerceLayouts'),
        registerReportMessages(locale),
        registerCommerceMessages(locale),
      ])
      registerCommerceLayouts()
    },
  },
  {
    // v3 path parity: `/budget-performance/performance-overview` (`router/routes/app.js:81–84`),
    // flat under the authenticated parent like 08's `budget-performance/budget-allocation`.
    // No layout registration: the page is the bespoke pivot workbench, not an 08 `ReportPage`.
    path: 'budget-performance/performance-overview',
    name: 'PerformanceOverview',
    component: () => import('./components/pages/PerformanceOverviewPage.vue'),
    meta: {
      navKey: 'performanceOverview',
      titleKey: 'commerceAnalytics.performanceOverview.title',
    },
    beforeEnter: async () => {
      const locale = activeLocale()
      // 08's bundle too: the toolbar's `ReportFilters` and the date picker render `reports.*`
      // copy, and on a cold deep link 08's own routes never ran.
      await Promise.all([registerReportMessages(locale), registerCommerceMessages(locale)])
    },
  },
  {
    // Retained legacy path/name; the custom page composes report 67 with optional report 53.
    path: 'marketing-insights/customer-intelligence',
    name: 'CustomerIntelligence',
    component: () => import('./components/pages/CustomerIntelligencePage.vue'),
    meta: {
      navKey: 'customerIntelligence',
      titleKey: 'commerceAnalytics.customerIntelligence.title',
    },
    beforeEnter: async () => {
      const locale = activeLocale()
      const [{ registerCommerceLayouts }] = await Promise.all([
        import('./flows/registerCommerceLayouts'),
        registerReportMessages(locale),
        registerCommerceMessages(locale),
      ])
      registerCommerceLayouts()
    },
  },
  {
    // v3 path + name parity (`router/index.js` `CreativeWatchlist`) — ORP-18955.
    path: 'creative-watchlist',
    name: 'CreativeWatchlist',
    component: () => import('./components/pages/CreativeWatchlistPage.vue'),
    meta: {
      navKey: 'creativeWatchlist',
      titleKey: 'commerceAnalytics.watchlist.title',
    },
    beforeEnter: async () => {
      const locale = activeLocale()
      const [{ registerCommerceLayouts }] = await Promise.all([
        import('./flows/registerCommerceLayouts'),
        registerReportMessages(locale),
        registerCommerceMessages(locale),
      ])
      registerCommerceLayouts()
    },
  },
]
